/**
 * NoFee categories — single source of truth for marketplace taxonomy.
 *
 * Each category has:
 *   - id (kebab-case slug, used in URLs like /category/<id>)
 *   - label (human-readable display name)
 *   - emoji (premium icon)
 *   - group (used in the "View all" modal to organize)
 *   - aliases (substrings used to fuzzy-match a restaurant.cuisine_type to this
 *     category — keeps filtering forgiving even when restaurants use loose tags)
 *   - featured (boolean — shows on the marketplace landing strip)
 *
 * Categories drive:
 *   - the featured home strip
 *   - the "Browse All" modal
 *   - SEO-friendly category pages at /category/<id>
 *   - filtering against the existing backend /public/restaurants?cuisine=
 */

export const CATEGORY_GROUPS = [
  'Popular',
  'American',
  'Asian',
  'Latin & Caribbean',
  'European',
  'African & Middle Eastern',
  'Healthy & Diet',
  'Drinks & Cafe',
  'Sweets & Bakery',
  'Bowls & Wraps',
  'Occasion',
];

export const CATEGORIES = [
  // ── POPULAR / FEATURED ─────────────────────────────────────────────────
  { id: 'pizza', label: 'Pizza', emoji: '🍕', group: 'Popular', featured: true, aliases: ['pizza', 'italian'] },
  { id: 'burgers', label: 'Burgers', emoji: '🍔', group: 'Popular', featured: true, aliases: ['burger', 'american'] },
  { id: 'sushi', label: 'Sushi', emoji: '🍣', group: 'Popular', featured: true, aliases: ['sushi', 'japanese'] },
  { id: 'mexican', label: 'Mexican', emoji: '🌮', group: 'Popular', featured: true, aliases: ['mexican', 'tex-mex'] },
  { id: 'healthy', label: 'Healthy', emoji: '🥗', group: 'Popular', featured: true, aliases: ['healthy', 'salad'] },
  { id: 'coffee', label: 'Coffee', emoji: '☕', group: 'Popular', featured: true, aliases: ['coffee', 'cafe'] },
  { id: 'desserts', label: 'Desserts', emoji: '🍰', group: 'Popular', featured: true, aliases: ['dessert', 'sweets'] },
  { id: 'bbq', label: 'BBQ', emoji: '🍗', group: 'Popular', featured: true, aliases: ['bbq', 'barbecue'] },
  { id: 'chicken', label: 'Chicken', emoji: '🍗', group: 'Popular', featured: true, aliases: ['chicken', 'wings'] },
  { id: 'breakfast', label: 'Breakfast', emoji: '🥞', group: 'Popular', featured: true, aliases: ['breakfast', 'brunch'] },
  { id: 'tacos', label: 'Tacos', emoji: '🌮', group: 'Popular', featured: true, aliases: ['taco'] },
  { id: 'sandwiches', label: 'Sandwiches', emoji: '🥪', group: 'Popular', featured: true, aliases: ['sandwich', 'sub'] },

  // ── AMERICAN ──────────────────────────────────────────────────────────
  { id: 'american', label: 'American', emoji: '🇺🇸', group: 'American', aliases: ['american'] },
  { id: 'fast-food', label: 'Fast Food', emoji: '🍟', group: 'American', aliases: ['fast', 'quick'] },
  { id: 'wings', label: 'Wings', emoji: '🍗', group: 'American', aliases: ['wing'] },
  { id: 'hot-dogs', label: 'Hot Dogs', emoji: '🌭', group: 'American', aliases: ['hot dog'] },
  { id: 'comfort-food', label: 'Comfort Food', emoji: '🍲', group: 'American', aliases: ['comfort'] },
  { id: 'soul-food', label: 'Soul Food', emoji: '🍛', group: 'American', aliases: ['soul'] },
  { id: 'southern-food', label: 'Southern Food', emoji: '🍳', group: 'American', aliases: ['southern'] },
  { id: 'cajun', label: 'Cajun', emoji: '🦐', group: 'American', aliases: ['cajun', 'creole'] },
  { id: 'steakhouse', label: 'Steakhouse', emoji: '🥩', group: 'American', aliases: ['steak'] },
  { id: 'seafood', label: 'Seafood', emoji: '🦞', group: 'American', aliases: ['seafood', 'fish'] },
  { id: 'hawaiian', label: 'Hawaiian', emoji: '🌺', group: 'American', aliases: ['hawaiian'] },
  { id: 'street-food', label: 'Street Food', emoji: '🥡', group: 'American', aliases: ['street'] },

  // ── ASIAN ─────────────────────────────────────────────────────────────
  { id: 'japanese', label: 'Japanese', emoji: '🍱', group: 'Asian', aliases: ['japanese'] },
  { id: 'chinese', label: 'Chinese', emoji: '🥢', group: 'Asian', aliases: ['chinese'] },
  { id: 'thai', label: 'Thai', emoji: '🍜', group: 'Asian', aliases: ['thai'] },
  { id: 'korean', label: 'Korean', emoji: '🇰🇷', group: 'Asian', aliases: ['korean', 'kbbq'] },
  { id: 'indian', label: 'Indian', emoji: '🍛', group: 'Asian', aliases: ['indian'] },
  { id: 'ramen', label: 'Ramen', emoji: '🍜', group: 'Asian', aliases: ['ramen'] },
  { id: 'pho', label: 'Pho', emoji: '🍲', group: 'Asian', aliases: ['pho', 'vietnamese'] },
  { id: 'noodles', label: 'Noodles', emoji: '🍝', group: 'Asian', aliases: ['noodle'] },
  { id: 'dumplings', label: 'Dumplings', emoji: '🥟', group: 'Asian', aliases: ['dumpling'] },
  { id: 'asian-fusion', label: 'Asian Fusion', emoji: '🍱', group: 'Asian', aliases: ['fusion', 'asian'] },
  { id: 'poke', label: 'Poke', emoji: '🥗', group: 'Asian', aliases: ['poke'] },

  // ── LATIN & CARIBBEAN ─────────────────────────────────────────────────
  { id: 'brazilian', label: 'Brazilian', emoji: '🇧🇷', group: 'Latin & Caribbean', aliases: ['brazilian', 'brasileir'] },
  { id: 'argentinian', label: 'Argentinian', emoji: '🥩', group: 'Latin & Caribbean', aliases: ['argentin'] },
  { id: 'peruvian', label: 'Peruvian', emoji: '🌽', group: 'Latin & Caribbean', aliases: ['peruvian'] },
  { id: 'colombian', label: 'Colombian', emoji: '🇨🇴', group: 'Latin & Caribbean', aliases: ['colombian'] },
  { id: 'cuban', label: 'Cuban', emoji: '🇨🇺', group: 'Latin & Caribbean', aliases: ['cuban'] },
  { id: 'venezuelan', label: 'Venezuelan', emoji: '🫓', group: 'Latin & Caribbean', aliases: ['venezuelan', 'arepa'] },
  { id: 'dominican', label: 'Dominican', emoji: '🇩🇴', group: 'Latin & Caribbean', aliases: ['dominican'] },
  { id: 'caribbean', label: 'Caribbean', emoji: '🌴', group: 'Latin & Caribbean', aliases: ['caribbean', 'jamaican'] },
  { id: 'latin-american', label: 'Latin American', emoji: '🌎', group: 'Latin & Caribbean', aliases: ['latin'] },
  { id: 'tortas', label: 'Tortas', emoji: '🥖', group: 'Latin & Caribbean', aliases: ['torta'] },
  { id: 'burritos', label: 'Burritos', emoji: '🌯', group: 'Latin & Caribbean', aliases: ['burrito'] },
  { id: 'empanadas', label: 'Empanadas', emoji: '🥟', group: 'Latin & Caribbean', aliases: ['empanada'] },

  // ── EUROPEAN ──────────────────────────────────────────────────────────
  { id: 'italian', label: 'Italian', emoji: '🇮🇹', group: 'European', aliases: ['italian'] },
  { id: 'french', label: 'French', emoji: '🥐', group: 'European', aliases: ['french'] },
  { id: 'greek', label: 'Greek', emoji: '🥙', group: 'European', aliases: ['greek'] },
  { id: 'german', label: 'German', emoji: '🥨', group: 'European', aliases: ['german'] },
  { id: 'portuguese', label: 'Portuguese', emoji: '🇵🇹', group: 'European', aliases: ['portuguese'] },
  { id: 'turkish', label: 'Turkish', emoji: '🌯', group: 'European', aliases: ['turkish', 'kebab'] },
  { id: 'mediterranean', label: 'Mediterranean', emoji: '🫒', group: 'European', aliases: ['mediterranean'] },

  // ── AFRICAN & MIDDLE EASTERN ─────────────────────────────────────────
  { id: 'middle-eastern', label: 'Middle Eastern', emoji: '🌯', group: 'African & Middle Eastern', aliases: ['middle eastern', 'arab'] },
  { id: 'halal', label: 'Halal', emoji: '🕌', group: 'African & Middle Eastern', aliases: ['halal'] },
  { id: 'kosher', label: 'Kosher', emoji: '🕎', group: 'African & Middle Eastern', aliases: ['kosher'] },
  { id: 'african', label: 'African', emoji: '🌍', group: 'African & Middle Eastern', aliases: ['african'] },
  { id: 'ethiopian', label: 'Ethiopian', emoji: '🇪🇹', group: 'African & Middle Eastern', aliases: ['ethiopian'] },

  // ── HEALTHY & DIET ────────────────────────────────────────────────────
  { id: 'vegan', label: 'Vegan', emoji: '🌱', group: 'Healthy & Diet', aliases: ['vegan'] },
  { id: 'vegetarian', label: 'Vegetarian', emoji: '🥦', group: 'Healthy & Diet', aliases: ['vegetarian'] },
  { id: 'gluten-free', label: 'Gluten-Free', emoji: '🌾', group: 'Healthy & Diet', aliases: ['gluten'] },
  { id: 'organic', label: 'Organic', emoji: '🥬', group: 'Healthy & Diet', aliases: ['organic'] },
  { id: 'salads', label: 'Salads', emoji: '🥗', group: 'Healthy & Diet', aliases: ['salad'] },
  { id: 'protein-meals', label: 'Protein Meals', emoji: '🍖', group: 'Healthy & Diet', aliases: ['protein'] },
  { id: 'meal-prep', label: 'Meal Prep', emoji: '🥘', group: 'Healthy & Diet', aliases: ['meal prep'] },

  // ── DRINKS & CAFE ─────────────────────────────────────────────────────
  { id: 'cafe', label: 'Cafe', emoji: '☕', group: 'Drinks & Cafe', aliases: ['cafe', 'coffee'] },
  { id: 'tea', label: 'Tea', emoji: '🫖', group: 'Drinks & Cafe', aliases: ['tea'] },
  { id: 'bubble-tea', label: 'Bubble Tea', emoji: '🧋', group: 'Drinks & Cafe', aliases: ['bubble', 'boba'] },
  { id: 'smoothies', label: 'Smoothies', emoji: '🥤', group: 'Drinks & Cafe', aliases: ['smoothie'] },
  { id: 'juice-bar', label: 'Juice Bar', emoji: '🧃', group: 'Drinks & Cafe', aliases: ['juice'] },
  { id: 'drinks', label: 'Drinks', emoji: '🍹', group: 'Drinks & Cafe', aliases: ['drink', 'beverage'] },
  { id: 'milkshakes', label: 'Milkshakes', emoji: '🥤', group: 'Drinks & Cafe', aliases: ['milkshake', 'shake'] },

  // ── SWEETS & BAKERY ───────────────────────────────────────────────────
  { id: 'bakery', label: 'Bakery', emoji: '🥐', group: 'Sweets & Bakery', aliases: ['bakery', 'bread'] },
  { id: 'ice-cream', label: 'Ice Cream', emoji: '🍦', group: 'Sweets & Bakery', aliases: ['ice cream'] },
  { id: 'frozen-yogurt', label: 'Frozen Yogurt', emoji: '🍨', group: 'Sweets & Bakery', aliases: ['frozen yogurt', 'froyo'] },
  { id: 'donuts', label: 'Donuts', emoji: '🍩', group: 'Sweets & Bakery', aliases: ['donut', 'doughnut'] },
  { id: 'cookies', label: 'Cookies', emoji: '🍪', group: 'Sweets & Bakery', aliases: ['cookie'] },
  { id: 'cheesecake', label: 'Cheesecake', emoji: '🍰', group: 'Sweets & Bakery', aliases: ['cheesecake'] },
  { id: 'acai-bowls', label: 'Acai Bowls', emoji: '🍓', group: 'Sweets & Bakery', aliases: ['acai'] },

  // ── BOWLS & WRAPS ─────────────────────────────────────────────────────
  { id: 'rice-bowls', label: 'Rice Bowls', emoji: '🍚', group: 'Bowls & Wraps', aliases: ['rice bowl'] },
  { id: 'organic-bowls', label: 'Organic Bowls', emoji: '🥗', group: 'Bowls & Wraps', aliases: ['organic bowl'] },
  { id: 'wraps', label: 'Wraps', emoji: '🌯', group: 'Bowls & Wraps', aliases: ['wrap'] },
  { id: 'loaded-fries', label: 'Loaded Fries', emoji: '🍟', group: 'Bowls & Wraps', aliases: ['loaded fries'] },

  // ── OCCASION ──────────────────────────────────────────────────────────
  { id: 'family-meals', label: 'Family Meals', emoji: '👨‍👩‍👧', group: 'Occasion', aliases: ['family'] },
  { id: 'late-night-food', label: 'Late Night Food', emoji: '🌙', group: 'Occasion', aliases: ['late night'] },
  { id: 'kids-meals', label: 'Kids Meals', emoji: '🧒', group: 'Occasion', aliases: ['kids'] },
  { id: 'party-platters', label: 'Party Platters', emoji: '🎉', group: 'Occasion', aliases: ['party'] },
  { id: 'fusion-cuisine', label: 'Fusion Cuisine', emoji: '🌐', group: 'Occasion', aliases: ['fusion'] },
  { id: 'spicy-food', label: 'Spicy Food', emoji: '🌶️', group: 'Occasion', aliases: ['spicy'] },
];

export const FEATURED_CATEGORIES = CATEGORIES.filter((c) => c.featured);

export const findCategory = (id) => CATEGORIES.find((c) => c.id === id) || null;

/**
 * Find the best-matching category for a freeform restaurant.cuisine_type string.
 * Returns null when no plausible match. Forgiving — uses substring matching
 * against each category's aliases.
 */
export const matchCategoryFromCuisine = (cuisine) => {
  if (!cuisine) return null;
  const c = cuisine.toLowerCase();
  return CATEGORIES.find((cat) => cat.aliases.some((a) => c.includes(a))) || null;
};
